// Desde el 2026-10-04 hay tres roles (lib/roles.js): estudiante, enfermero y
// TENS. Docencia y postgrado son enfermero; el internado es la ETAPA del
// estudiante (`etapa_internado`), no un rol. Por eso aquí ya no hay ramas para
// postgrado ni para el valor legado 'interno': la BD los traduce al escribir.

/**
 * ¿El perfil corresponde a un interno en rotación activa (o ya ejerce)?
 * Gatea superficies de práctica REAL (p. ej. la entrega SBAR real vs práctica).
 * Desde 2026-09-23 NO gatea la Bitácora (la escribe cualquier perfil) ni su
 * modo internado (eso es `veModoInternado`, que excluye a enfermeros).
 * @param {object} profile
 * @returns {boolean}
 */
export function esInternoActivo(profile) {
  return (
    profile?.rol === 'enfermero' ||
    (profile?.rol === 'estudiante' &&
     profile?.etapa_internado === 'internado_en_curso')
  );
}

// ¿Ve el MODO INTERNADO de la Bitácora (focos de formación plegados y diario de
// campo)? Solo quien está en internado: es su artefacto académico. NO es
// `esInternoActivo`, que incluye a enfermeros/as porque gatea otras cosas
// (entrega SBAR real): a un profesional en ejercicio no se le habla de "tu
// tutor/a" (auditoría 2026-09-23).
export function veModoInternado(profile) {
  return profile?.rol === 'estudiante' && profile?.etapa_internado === 'internado_en_curso';
}

// ¿El perfil es de la capa EJERCICIO (enfermero/a, incluida docencia y postgrado)?
// Se usa para NO empujarle herramientas de formación: el pro quiere velocidad,
// no un diario. La Bitácora ya no es artefacto exclusivo del internado (desde el
// 2026-09-23 es para procesar el turno, abierta a todo perfil), pero el
// autocuidado no se empuja: está disponible si la busca, jamás como tarea.
export function esCapaEjercicio(profile) {
  return profile?.rol === 'enfermero';
}

// Etapas de estudiante cuya capa gira en torno al TURNO (aterrizan en MiPAE en turno,
// ven el héroe/calendario del turno, reciben nudges de turno). Decisión de
// producto (auditoría de capas 2026-07-04): la PRÁCTICA DE PREGRADO tiene foco
// PAE, no turno — antes estaba aquí y quedaba con identidad fracturada
// (esElegibleTurno la incluía pero esInternoActivo la excluía, generando
// callejones sin salida en Bitácora/PreparaTurno). Ahora solo el internado en
// curso. La pestaña "En turno" sigue visible para todos, pero al estudiante sin
// internado en curso le abre una presentación corta de MiPAE en turno con la
// opción de probarlo con un paciente ficticio (veEnTurnoEstudiante, decisión
// del fundador 2026-10-04); no es su superficie primaria.
const ETAPAS_CON_TURNO = ['internado_en_curso'];

/**
 * ¿El perfil debe ver las herramientas de turno como superficie PRIMARIA
 * (aterrizaje en MiPAE en turno, héroe/calendario del turno, nudges)? Hoy coincide
 * con esInternoActivo (ambas excluyen la práctica de pregrado), pero se
 * mantienen separadas por claridad semántica: esInternoActivo gatea las
 * superficies de práctica real (entrega SBAR), esElegibleTurno las de turno.
 * @param {object} profile
 * @returns {boolean}
 */
export function esElegibleTurno(profile) {
  return (
    profile?.rol === 'enfermero' ||
    (profile?.rol === 'estudiante' &&
     ETAPAS_CON_TURNO.includes(profile?.etapa_internado))
  );
}


/**
 * ¿MiPAE en turno le abre la PRESENTACIÓN en vez del centro de control?
 * Decisión del fundador 2026-10-04 (P1 de la auditoría del estudiante): al
 * estudiante que no está en internado en curso (práctica de pregrado,
 * internado por empezar o ya terminado, o sin etapa) se le explica para qué
 * sirve y se le ofrece probarlo con un paciente ficticio. Interno/a en curso,
 * enfermero/a y TENS entran como siempre. Es el complemento exacto de
 * esElegibleTurno dentro del rol estudiante (misma fuente: ETAPAS_CON_TURNO).
 * @param {object} profile
 * @returns {boolean}
 */
export function veEnTurnoEstudiante(profile) {
  return profile?.rol === 'estudiante' && !esElegibleTurno(profile);
}

/**
 * ¿El perfil ve "Tus PAEs" como pantalla propia (lista completa con buscador
 * y filtro, tile en el Home)? Decisión del fundador 2026-09-10: SOLO
 * estudiantes e internos, porque el PAE es la herramienta de formación.
 * Desde el 2026-10-16 el PAE vive solo en MiPAE Estudiante: enfermeros y TENS
 * abren sus PAEs guardados pasando a ese mundo (con el perfil del mundo esta
 * función da true), y Mi perfil de su casa ya no los lista.
 * @param {object} profile
 * @returns {boolean}
 */
export function veMisPaes(profile) {
  return profile?.rol === 'estudiante';
}

/**
 * Las filas de "Lo tuyo" en Mi perfil, en orden, según la CASA (I5 del plan
 * de la interfaz de los tres mundos, decisión del fundador 2026-10-04). Mi
 * perfil habla del perfil real, nunca del mundo visitado.
 *   estudiante              → Tus PAEs · Mi progreso · Bitácora · Ajustes
 *   estudiante en internado → Tus PAEs · Mi progreso · Mi calendario · Bitácora · Ajustes
 *   enfermero/a y TENS      → Mi calendario · Bitácora · Ajustes
 * Mi calendario sigue la regla de la actividad `turno` de la racha
 * (`actividadesRacha`): a una estudiante sin turnos le decía "Configura tu
 * rotación de turnos" sin tener ninguno. La Bitácora va en los tres mundos
 * (abierta a todo perfil desde el 2026-09-23); es fila disponible, jamás aviso.
 * @param {object} profile  perfil real
 * @returns {Array<'paes'|'progreso'|'calendario'|'bitacora'|'ajustes'>}
 */
export function filasLoTuyo(profile) {
  const formacion = profile?.rol !== 'enfermero' && profile?.rol !== 'tens';
  const filas = [];
  if (veMisPaes(profile)) filas.push('paes');
  if (formacion) filas.push('progreso');
  // esTens vive en lib/tens.js; aquí la misma comparación para no cruzar
  // módulos por una línea.
  if (esElegibleTurno(profile) || profile?.rol === 'tens') filas.push('calendario');
  filas.push('bitacora', 'ajustes');
  return filas;
}

/**
 * ¿Puede abrir el tablero de la jefatura (/jefatura)? Admin y owner siempre;
 * además quien tenga el permiso acotado `ve_jefatura` en su perfil (concedido
 * a mano, migración 20260910200000). Nació el 2026-09-10 para habilitar a
 * una jefatura real sin hacerla admin de MiPAE: admin abriría Métricas y la
 * tabla de usuarios de toda la plataforma, y esto es solo una pantalla.
 * @param {object} profile
 * @returns {boolean}
 */
export function veJefatura(profile) {
  if (!profile) return false;
  return profile.role === 'admin' || profile.role === 'owner' || profile.ve_jefatura === true;
}

/**
 * ¿Ve la pantalla de Diálisis (/dialisis)? Es la herramienta del team de
 * terapias extracorpóreas: conectar una TRR es un acto de enfermería, así que
 * la ven los roles EN EJERCICIO y no la capa de formación, que no conecta.
 * Coincide hoy con `esCapaEjercicio` y se mantiene aparte por claridad
 * semántica, misma disciplina que `esElegibleTurno` contra `esInternoActivo`:
 * si mañana el team suma otro rol, se cambia acá y no en la definición de capa.
 * @param {object} profile
 * @returns {boolean}
 */
export function veDialisis(profile) {
  return esCapaEjercicio(profile);
}
